import { Box, Button, Skeleton, Stack, Typography } from '@mui/material'
import CaseTime from '../../components/common/CaseTime'
import Pill from '../../components/common/Pill'
import { labelFrom, UNMAPPED_LABEL_HINT } from '../../components/common/labelFrom'
import { FS, SP } from '../../theme/tokens'
import useAgentZahlen from './hooks/useAgentZahlen'
import useAgentBefunde from './hooks/useAgentBefunde'

// 🔴 Die Schwere-Tabelle, zum zweiten Mal. Die erste steht in `BefundeCard`
// (Fallansicht), die dritte in `pages/invoicing/Betrieb.jsx` (ein anderer
// Wortschatz, `sperre|hinweis`). Ein geteiltes Bauteil gibt es nicht, und dieser
// Schnitt legt keines an: das Zusammenziehen fasst zwei fremde Flaechen an,
// waehrend eine parallele Bahn an denselben Fallansichten arbeitet.
//
// 🔴 **Warum die ZWEI Tabellen hier stehen und nicht eine.** Der Ton ist eine
// Anzeigeentscheidung dieser Flaeche, das Etikett ist der Wortlaut. Sie fallen
// nur zufaellig zusammen: `niedrig` traegt keinen Ton (die Plakette traegt den
// Text ohnehin, Regel 22 Punkt 2) und sehr wohl ein Etikett.
//
// Der Wortschatz ist GESCHLOSSEN (CHECK in Migration 357: `hoch|mittel|niedrig`),
// beide Tabellen decken ihn also vollstaendig. Gelesen wird er trotzdem ueber
// `labelFrom` und den Rohwert-Rueckfall auf `plain`: ein vierter Wert soll als
// „nichts hervorzuheben" sichtbar bleiben und nicht verschwinden.
const SCHWERE_LABEL = { hoch: 'High', mittel: 'Medium', niedrig: 'Low' }
const SCHWERE_TON = { hoch: 'err', mittel: 'warn', niedrig: 'plain' }

const AUS = 'aus'
// `unbekannt` heisst: die Abfrage ist gescheitert. Das ist NICHT "aus".
const UNBEKANNT = 'unbekannt'

/** Eine Zeile im Zahlenblock. Der Schluessel ist noetig, die Farbe optional. */
const zeile = (text, key, farbe = 'text.secondary') => (
  <Typography key={key} sx={{ fontSize: FS.xs, color: farbe }}>{text}</Typography>
)

/**
 * AgentZahlen — der Tagesstand des Agenten, direkt unter seinem Schalter
 * (N102, A7) — und seit B3 das, was er GEFUNDEN hat.
 *
 * @description
 *   Zwei Bloecke, ein Ort:
 *
 *   1. `ZahlenBlock`: Laeufe heute (fertig / abgebrochen), offene Anstoesse,
 *      verfallene Anstoesse nach Grund, die Kosten des Tages und die geltenden
 *      Deckel. Sie steht neben `agent_anstoss_aktiv`, weil der Owner beim
 *      Umlegen genau diese Zahlen braucht: ob der Agent ueberhaupt laeuft, ob
 *      etwas liegenbleibt und ob ein Deckel greift.
 *   2. `BefundeBlock` (B3): was der Agent gefunden hat — gefeuerte Pruefungen mit
 *      offenen Befunden und Fehlalarmquote je Pruefung, darunter die offenen
 *      Befunde OHNE Fall (Bestandslauf) als kurze Liste.
 *
 *   🔴 **Die Deckel-Zeile wird nicht nachgerechnet.** Sie kommt woertlich aus
 *   `GET /api/agent/zahlen` (`abarbeiter._deckel_halt`) — derselben Funktion, die
 *   der Takt benutzt. Eine zweite Meinung in der Oberflaeche waere eine
 *   Behauptung ueber den Arbeiter statt seines Zustands.
 *
 *   🔴 **Eine Null ist hier nie eine Behauptung.** Kosten stehen nur dann als
 *   Betrag da, wenn es ueberhaupt Laeufe gab; sonst heisst es „keine Laeufe".
 *   Laeufe ohne gemessenen Preis werden als eigene Zahl daneben genannt
 *   (`unbepreist_heute`), weil `agent_runs.cost_usd` NULL bleibt, solange kein
 *   Preis gemessen werden konnte — eine Summe ohne diese Zahl saehe aus wie
 *   „kostet nichts". Dieselbe Regel traegt den Befundblock: eine Fehlalarmquote
 *   ohne gefeuerte Befunde ist `null` und steht als „no rate yet" da, nie als
 *   „0 %".
 *
 *   🔴 **Die zwei Bloecke holen ihre Daten GETRENNT** (`useAgentZahlen`,
 *   `useAgentBefunde`) und werden als Geschwister ohne gemeinsamen Kasten
 *   gerendert. Der Grund ist nicht Geschmack: die Zahlen aendern sich im Takt
 *   des Arbeiters, die Befundlage nur, wenn ein Lauf schreibt — und ein
 *   gemeinsamer Fehlerzustand haette einen Ausfall der Befundabfrage zu einem
 *   Ausfall der Tageszahlen gemacht. Faellt einer der zwei Abrufe aus, steht
 *   seine Flaeche mit eigener Aussage da, die andere bleibt.
 *
 * @useWhen
 *   - In `AdminSettings`, unmittelbar unter der Zeile `agent_anstoss_aktiv`
 *
 * @dontUseFor
 *   - Als Verlaufsgrafik oder Auswertung: das ist der Tagesstand, kein Bericht
 *   - Am Fall: dort steht `BefundeCard` mit dem Umfang EINES Falls
 *
 * @related
 *   - useAgentZahlen / useAgentBefunde: die Abrufe; diese Anzeige holt nichts selbst
 *   - BefundeCard: dieselbe Lesung, aber am Fall und mit dem Wegklick
 *   - AdminSettings: der Ort, an dem sie steht
 *
 * @reuseHints
 *   - Kein weiterer Ort geplant
 */
export default function AgentZahlen({ version = 0 }) {
  const { zahlen, loading, error, reload } = useAgentZahlen(version)
  const {
    fehlalarme, bestand, loading: befundeLaden, error: befundeFehler, reload: befundeNeu,
  } = useAgentBefunde()

  // Geschwister ohne Huelle: sind beide leer, bleibt der Baum leer (die Probe
  // „gar nichts geladen rendert nichts" haengt daran).
  return (
    <>
      <ZahlenBlock zahlen={zahlen} loading={loading} error={error} reload={reload} />
      <BefundeBlock
        fehlalarme={fehlalarme}
        bestand={bestand}
        loading={befundeLaden}
        error={befundeFehler}
        reload={befundeNeu}
      />
    </>
  )
}

/**
 * Der Tagesstand. Unveraendert seit A7 — die Auslagerung ist reine Bauform,
 * keine Inhaltsaenderung: dieselben Kennungen, dieselben Saetze.
 */
function ZahlenBlock({ zahlen, loading, error, reload }) {
  if (loading && !zahlen) {
    return (
      <Box sx={{ px: SP.card, py: SP.tight }} data-testid="agent-zahlen-loading">
        <Skeleton variant="text" width="55%" height={FS.lg} />
        <Skeleton variant="text" width="35%" height={FS.lg} />
      </Box>
    )
  }

  if (error) {
    return (
      <Box sx={{ px: SP.card, py: SP.tight }} data-testid="agent-zahlen-error">
        <Stack direction="row" spacing={SP.tight} alignItems="center">
          <Typography sx={{ fontSize: FS.xs, color: 'text.secondary' }}>
            Agent numbers unavailable. This says nothing about the agent.
          </Typography>
          <Button size="small" variant="text" onClick={reload} data-testid="agent-zahlen-wiederholen">
            Try again
          </Button>
        </Stack>
      </Box>
    )
  }

  if (!zahlen) return null

  const l = zahlen.laeufe_heute || {}
  const gesamt = l.gesamt || 0
  const teile = [
    `${l.done || 0} done`,
    `${l.aborted || 0} aborted`,
  ]
  if (l.failed) teile.push(`${l.failed} failed`)
  if (l.running) teile.push(`${l.running} running`)

  const verfallen = zahlen.verfallen || {}
  const verfallTeile = []
  if (verfallen.verfallen_schalter_aus) {
    verfallTeile.push(`${verfallen.verfallen_schalter_aus} with the switch off`)
  }
  if (verfallen.verfallen_alter) {
    verfallTeile.push(`${verfallen.verfallen_alter} too old`)
  }

  return (
    <Box sx={{ px: SP.card, py: SP.tight }} data-testid="agent-zahlen">
      <Stack spacing={SP.chip}>
        {zeile(
          gesamt > 0 ? `Runs today: ${teile.join(' · ')}` : 'Runs today: none yet.',
          'laeufe',
        )}
        {zeile(`Open triggers: ${zahlen.offene_anstosse || 0}`, 'offen')}
        {zeile(
          verfallTeile.length
            ? `Expired: ${zahlen.verfallen_gesamt} (${verfallTeile.join(', ')})`
            : `Expired: ${zahlen.verfallen_gesamt || 0}`,
          'verfallen',
        )}
        {zeile(
          gesamt > 0
            ? `Cost today: $${zahlen.kosten_heute_usd}`
              + (zahlen.unbepreist_heute ? ` · ${zahlen.unbepreist_heute} without a price` : '')
            : 'Cost today: no runs, so nothing measured.',
          'kosten',
        )}
        {zeile(
          `Daily caps: ${zahlen.deckel_laeufe} runs / $${zahlen.deckel_kosten_usd}`,
          'deckel',
        )}

        {/* N102 C2c: was der Agent JE REPARATUR getan hat. Eine Zeile je
            Reparatur, dieselbe Bauform wie die Pruefungszeilen darunter — kein
            Balken, keine Farbe: die Zahl ist die Aussage, nicht ihre Laenge.
            🔴 Fehlt der Abschnitt ganz, sagt die Zeile das, statt eine leere
            Flaeche zu zeigen: „keine Reparatur" und „nicht mitgeliefert" sind
            zwei Aussagen (Regel 22 § 3). */}
        {!Array.isArray(zahlen.reparaturen)
          && zeile(
            `Per repair: ${STRICH} (this endpoint build does not report it)`,
            'reparaturen-fehlt',
          )}

        {(zahlen.reparaturen || []).map((r) => {
          const schalter = labelFrom(SCHALTER_SATZ, r.schalter)
          // 🔴 Auch die zwei Ruecknahme-Zahlen gehoeren hierher (Regel-16-Befund
          // W1). Fehlt `fertig`, sagt der Satz darunter "not reported" — und
          // dieselbe Zeile muss das als fehlende Zahl kennzeichnen, sonst
          // widerspricht der Zusatz dem Satz daneben.
          const unvollstaendig = typeof r.aktionen_heute !== 'number'
            || typeof r.ohne_wirkung !== 'number'
            || typeof r.fertig !== 'number'
            || typeof r.zurueckgenommen !== 'number'
            || typeof r.aktionen_ruhend !== 'number'
          // 🔴 **Drei Zahlen, drei Fenster, und jedes steht in seinem Satz**
          // (Grill-Wiedervorlage 1, W6). „today" ist der Berliner Tag,
          // „resting now" der Zustand in diesem Moment, „all time" der Bestand
          // ueber alle vorhandenen Zeilen. Ohne das Fenster las man dreimal
          // „without effect" und hielt sie fuer dieselbe Groesse.
          const teile = [
            `${zahlText(r.aktionen_heute)} today`,
            ruecknahmeSatz(r),
            `${zahlText(r.ohne_wirkung)} without effect, all time`,
            `${zahlText(r.aktionen_ruhend)} resting now`,
            schalter.label,
          ]
          return (
            <Typography
              key={r.pruefung}
              data-testid={`agent-reparatur-${r.pruefung}`}
              data-schalter={r.schalter}
              sx={{ fontSize: FS.xs, color: 'text.secondary' }}
            >
              {[r.pruefung, r.titel || r.reparatur].filter(Boolean).join(' · ')}
              {': '}
              {teile.join(' · ')}
              {unvollstaendig ? ' · some numbers did not arrive' : ''}
            </Typography>
          )
        })}

        {zahlen.schalter === 'unbekannt'
          && zeile('The switch could not be read right now.', 'schalter', 'warning.main')}
        {zahlen.halt && zeile(zahlen.halt, 'halt', 'warning.main')}
      </Stack>
    </Box>
  )
}

/** Der Prozentwert einer Quote, die `null` sein darf. `null` bleibt `null`. */
function quoteText(quote) {
  if (quote === null || quote === undefined) return null
  return `${Math.round(quote * 100)}%`
}

/**
 * 🔴 Das Hauszeichen fuer „nicht gemessen": genau das Zeichen, das
 * `components/common/labelFrom.js` fuer einen leeren Wert liefert und das
 * `AuditFeed` ebenso schreibt. Hier steht es als eigene Konstante, weil diese
 * Flaeche es an Stellen setzt, an denen `labelFrom` nicht greift — eine fehlende
 * Zahl ist kein unbekannter Aufzaehlungswert.
 */
const STRICH = '—'

/**
 * Eine Zahl, die auch FEHLEN darf. Fehlt sie, steht der Strich — nicht die Null.
 *
 * 🔴 „null Aktionen" und „diese Zahl kam nicht mit" sind zwei verschiedene
 * Aussagen, und eine Anzeige, die beide als `0` malt, behauptet die erste
 * (Regel 22 § 3). Der Grund steht in der Zeile daneben, nicht in der Zahl.
 */
function zahlText(wert) {
  return typeof wert === 'number' ? String(wert) : STRICH
}

/** Der Schalterzustand je Reparatur in Worten — vier Lagen, nicht zwei. */
const SCHALTER_SATZ = {
  an: 'switch on',
  aus: 'switch off',
  unbekannt: 'switch not readable',
  keiner: 'no switch',
}

/**
 * Der Ruecknahme-Satz EINER Reparatur. VIER Faelle, und sie duerfen nicht
 * gleich aussehen:
 * - `fertig` FEHLT → Strich mit "not reported". 🔴 Regel-16-Befund W1: die
 *   frueher hier stehende Reihenfolge las `!r?.fertig` zuerst, und das trifft
 *   `undefined` wie `0`. Eine Zahl, die nicht mitkam, stand damit als "no
 *   completed action yet" da — eine Behauptung ueber den Bestand, wo in
 *   Wahrheit nichts gemessen wurde (Regel 22 § 3). Die Anwesenheit entscheidet
 *   zuerst, der Wert danach.
 * - nichts fertig (`fertig === 0`) → Strich mit Grund. Es gibt kein Verhaeltnis,
 *   also gibt es keine Zahl; `0 %` waere eine Behauptung ueber eine Messung,
 *   die nicht stattgefunden hat, und die Division durch null findet nicht statt.
 * - fertig, aber ohne Quote → Strich mit dem anderen Grund (der Endpunkt hat die
 *   Quote nicht mitgeschickt, etwa eine aeltere Fassung).
 * - sonst → die zwei Zahlen und der Prozentwert.
 */
function ruecknahmeSatz(r) {
  const quote = quoteText(r?.ruecknahmequote)
  if (typeof r?.fertig !== 'number') return `rollback ${STRICH} (not reported)`
  if (!r.fertig) return `rollback ${STRICH} (no completed action yet)`
  if (!quote) return `rollback ${STRICH} (not reported)`
  return `${r.zurueckgenommen} of ${r.fertig} rolled back (${quote})`
}

/**
 * Was der Agent gefunden hat (N102, B3).
 *
 * Zwei Abschnitte aus zwei Endpunkten:
 * - `GET /api/agent/fehlalarme`: je Pruefung gefeuert / offen / weggeklickt und
 *   die Quote. Gezeigt werden nur Pruefungen, die mindestens einmal gefeuert
 *   haben — eine Zeile „0 gefeuert" waere eine Aussage ueber das Register, nicht
 *   ueber den Agenten. Die Gesamtzahl steht als Satz darueber.
 * - `GET /api/agent/befunde/bestand`: die offenen Befunde OHNE Fall, nach
 *   Schwere sortiert, gedeckelt. Ist der Deckel erreicht, sagt die Zeile das.
 *
 * 🔴 **Die zwei Zahlen sind nicht dasselbe.** Die Quote zaehlt Befundzeilen ueber
 * ALLE Faelle, die Bestandsliste nur die ohne Fall. Der Hinweis unter der Quote
 * ist deshalb kein Beiwerk, sondern die halbe Aussage.
 *
 * 🔴 **Die Zaehlweise wird nicht behauptet, sondern gelesen.** Der Satz haengt an
 * `zaehlweise === 'befunde'`; aendert der Endpunkt seine Zaehlweise, verschwindet
 * der Satz, statt weiter etwas Falsches zu sagen.
 */
function BefundeBlock({ fehlalarme, bestand, loading, error, reload }) {
  if (loading && !fehlalarme && !bestand) {
    return (
      <Box sx={{ px: SP.card, py: SP.tight }} data-testid="agent-befunde-loading">
        <Skeleton variant="text" width="60%" height={FS.lg} />
        <Skeleton variant="text" width="40%" height={FS.lg} />
      </Box>
    )
  }

  if (error) {
    return (
      <Box sx={{ px: SP.card, py: SP.tight }} data-testid="agent-befunde-error">
        <Stack direction="row" spacing={SP.tight} alignItems="center">
          <Typography sx={{ fontSize: FS.xs, color: 'text.secondary' }}>
            Agent findings unavailable. This says nothing about the agent.
          </Typography>
          <Button size="small" variant="text" onClick={reload} data-testid="agent-befunde-wiederholen">
            Try again
          </Button>
        </Stack>
      </Box>
    )
  }

  if (!fehlalarme && !bestand) return null

  const pruefungen = fehlalarme?.pruefungen || []
  const gefeuert = pruefungen.filter((p) => (p.befunde || 0) > 0)
  const zaehlweise = fehlalarme?.zaehlweise

  const offen = bestand?.offen || []
  const bestandGesamt = bestand?.gesamt || 0
  const deckel = bestand?.deckel
  const schalter = bestand?.schalter

  // Der leere Grund des Bestandsabschnitts, drei Lagen wie in `BefundeCard`:
  // „Agent aus", „Schalter nicht lesbar" und „hingesehen, nichts gefunden"
  // duerfen nicht dreimal gleich aussehen.
  let bestandLeer
  if (schalter === UNBEKANNT) {
    bestandLeer = 'The agent switch could not be read, so nothing is claimed about findings without a case.'
  } else if (schalter === AUS) {
    bestandLeer = 'The agent is switched off, so no new findings are being written.'
  } else {
    bestandLeer = 'No open findings without a case right now.'
  }

  return (
    <Box sx={{ px: SP.card, py: SP.tight }} data-testid="agent-befunde">
      <Stack spacing={SP.sm}>
        {zeile(
          `Checks that fired: ${gefeuert.length} of ${pruefungen.length}`,
          'gefeuert',
        )}

        {gefeuert.map((p) => {
          const quote = quoteText(p.fehlalarmquote)
          const teile = [
            `${p.befunde} fired`,
            `${p.offen || 0} open`,
            quote
              ? `${p.kein_fehler} of ${p.befunde} dismissed (${quote})`
              : `${p.kein_fehler} dismissed, no rate yet`,
          ]
          return (
            <Typography
              key={p.pruefung}
              data-testid={`agent-pruefung-${p.pruefung}`}
              data-quelle={p.quelle}
              sx={{ fontSize: FS.xs, color: 'text.secondary' }}
            >
              {[p.pruefung, p.titel].filter(Boolean).join(' · ')}: {teile.join(' · ')}
            </Typography>
          )
        })}

        {gefeuert.length === 0 && zeile('No check has fired a finding yet.', 'keine')}

        {zaehlweise === 'befunde' && zeile(
          'Counted per finding row, not per trigger: a check keeps at most one open row per case, and a later run only moves "last seen".',
          'zaehlweise',
        )}

        <Box>
          <Typography sx={{ fontSize: FS.xs, color: 'text.secondary' }}>
            Without a case: {bestandGesamt} open
            {deckel && bestandGesamt > offen.length ? ` (showing the first ${deckel})` : ''}
          </Typography>
        </Box>

        {offen.length === 0 && zeile(bestandLeer, 'bestand-leer')}

        {offen.map((z) => {
          const schwere = labelFrom(SCHWERE_LABEL, z.schwere)
          return (
          <Box
            key={z.id}
            data-testid={`agent-bestand-${z.id}`}
            data-pruefung={z.pruefung}
          >
            <Stack spacing={SP.chip}>
              <Stack direction="row" spacing={SP.chip} alignItems="center" flexWrap="wrap" useFlexGap>
                {/* 🔴 Der Wortlaut, nicht der Rohwert. Bis B4 stand hier
                    `{z.schwere}`, also „mittel" auf einer Flaeche, deren eigener
                    Kommentar zwei Zeilen weiter unten sagt, sie sei durchgehend
                    englisch, waehrend die Fallansicht daneben „Medium" schrieb.
                    Dieselbe Groesse, zwei Sprachen, zwei Flaechen. */}
                <Pill
                  tone={SCHWERE_TON[z.schwere] || 'plain'}
                  title={schwere.unmapped ? UNMAPPED_LABEL_HINT : undefined}
                >
                  {schwere.label}
                </Pill>
                <Typography sx={{ fontSize: FS.xs, color: 'text.secondary' }}>
                  {[z.pruefung, z.titel].filter(Boolean).join(' · ')}
                </Typography>
                {/* Absolut, wie in `BefundeCard`: diese Flaeche ist durchgehend
                    englisch, und `CaseTime` rendert relative Zeiten deutsch. */}
                <CaseTime value={z.erstmals_gesehen} variant="absolute" />
              </Stack>
              <Typography sx={{ fontSize: FS.sm, color: 'text.primary' }}>{z.text}</Typography>
            </Stack>
          </Box>
          )
        })}
      </Stack>
    </Box>
  )
}
